import React, { useState, useEffect, FC } from "react";
import { Form, Table, Modal, Button, Typography, Row, Col } from "antd";
import type { FormInstance, TableColumnsType, TableProps } from "antd";
import Draggable from "react-draggable";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { BranchSelect } from "../../../components/forms/AutoCompleteFormItem";
import CheckBoxItem from "../../../components/forms/CheckBoxItem";
import { DatePickerItem } from "../../../components/forms/FormItems";
import AnimationChildWrapper from "../../../components/animations/AnimationChildWrapper";
import moment, { Moment } from "moment";
import { listProspectuses } from "../../../api/server";
import { useIntl } from "react-intl";
import { useOrganizationPermission } from "../../../hooks/accessControl";
import { Prospectus } from "../../../types/accounting";

const { Title } = Typography;

interface GenerateMasavReportModalProps {
  onHide: () => void;
  visible: boolean;
  title: string;
  btnText: string;
  initialValues?: FormValues;
  onSubmit: (payload: SubmitPayload) => void;
  branchId?: string | number;
  loading?: boolean;
  onChange?: (
    form: FormInstance<FormValues>,
    changedValues: Partial<FormValues>,
    allValues: FormValues
  ) => void;
}

interface FormValues {
  month?: Moment | null;
  add_prospectuses?: boolean;
  year?: Moment | null;
  incremental?: boolean;
  dry_run?: boolean;
  branchId?: boolean;
}

interface SubmitPayload {
  month?: number;
  year?: number;
  branches?: string[];
  all_branches?: boolean;
  incremental?: boolean;
  dry_run?: boolean;
  prospectuses?: (string | number)[];
  branchId?: boolean;
}

const GenerateMasavReportModal: FC<GenerateMasavReportModalProps> = (props) => {
  const {
    onHide,
    visible,
    title,
    btnText,
    initialValues = {},
    onSubmit,
    branchId,
    loading,
    onChange,
  } = props;
  const [values, setValues] = useState<FormValues>(initialValues);
  const [form] = Form.useForm<FormValues>();
  const [addProspectuses, setAddProspectuses] = useState<boolean>(
    initialValues.add_prospectuses || false
  );
  const [prospectuses, setProspectuses] = useState<Prospectus[]>([]);
  const [selectedProspectuses, setSelectedProspectuses] = useState<
    (string | number)[]
  >([]);
  const intl = useIntl();
  const hasOrgAccess = useOrganizationPermission();

  useEffect(() => {
    const currentInitialValues = initialValues || {};
    form.resetFields();
    form.setFieldsValue(currentInitialValues);
    if (!visible) {
      setAddProspectuses(false);
      setSelectedProspectuses([]);
      form.resetFields();
    }
  }, [visible, initialValues, form]);

  const onCancel = () => {
    onHide();
    form.resetFields();
    setAddProspectuses(false);
    setSelectedProspectuses([]);
    setValues({});
  };

  const onValuesChange = (
    changedValues: Partial<FormValues>,
    allValues: FormValues
  ) => {
    setValues(allValues);

    if (onChange) {
      onChange(form, changedValues, allValues);
    }

    if (changedValues.hasOwnProperty("add_prospectuses")) {
      const isAddingProspectuses = !!changedValues.add_prospectuses;
      setAddProspectuses(isAddingProspectuses);
      setSelectedProspectuses([]);
    }
  };

  const onFinish = (finishedValues: FormValues) => {
    const monthToFetch = finishedValues.month;
    let payload: SubmitPayload = {
      incremental: finishedValues.incremental,
      dry_run: finishedValues.dry_run,
      year: moment(monthToFetch).year(),
      month: moment(monthToFetch).month() + 1,
    };
    if (values.branchId) {
      payload = {
        ...payload,
        branchId: values.branchId,
      };
    }
    if (addProspectuses) {
      payload = {
        ...payload,
        prospectuses: selectedProspectuses,
      };
    } else {
      payload = {
        ...payload,
      };
    }

    Object.keys(payload).forEach((key) => {
      if (payload[key as keyof SubmitPayload] === undefined) {
        delete payload[key as keyof SubmitPayload];
      }
    });

    onSubmit(payload);
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log("Failed:", errorInfo);
  };

  const layout = {
    labelCol: { span: 12 },
    wrapperCol: { span: 12 },
  };

  const fetchProspectuses = async (yearValue?: Moment | null) => {
    const monthToFetch = yearValue || form.getFieldValue("month");
    if (!monthToFetch) {
      setProspectuses([]);
      return;
    }
    const branchPath = branchId ? `branches/${branchId}/` : "";
    const path = `${branchPath}accounting/prospectuses`;

    // Construct filters array, starting with the year
    const filters: Record<string, any>[] = [
      { year: { $eq: moment(monthToFetch).year() } },
      { month: { $eq: moment(monthToFetch).month() + 1 } },
      { is_self_participating: { $eq: true } }, // Filter for self-participating only
    ];

    try {
      const response = await listProspectuses({
        path: path,
        filter: { $and: filters }, // Use the combined filters
        limit: 1000,
      });
      const items = (response?.data?.items || []).map((item: any) => {
        const { children, ...rest } = item;
        return rest;
      });
      setProspectuses(items as Prospectus[]);
    } catch (error) {
      console.error("Error fetching prospectuses:", error);
      setProspectuses([]);
    }
  };

  useEffect(() => {
    const currentMonth = form.getFieldValue("month");
    if (addProspectuses && currentMonth) {
      fetchProspectuses(currentMonth);
    } else if (!addProspectuses) {
      setProspectuses([]);
    }
  }, [addProspectuses, form.getFieldValue("month")]);

  const columns: TableColumnsType<Prospectus> = [
    {
      title: intl.formatMessage({ id: "app.prospectuses.created_at" }),
      dataIndex: "created_at",
      key: "created_at",
      render: (text?: string | Date) =>
        text ? moment(text).format("DD/MM/YYYY") : "N/A",
    },
    {
      title: intl.formatMessage({ id: "app.prospectuses.year" }),
      dataIndex: "year",
      key: "year",
      render: (year?: number) => year || "N/A",
    },
    {
      title: intl.formatMessage({ id: "app.prospectuses.month" }),
      dataIndex: "month",
      key: "month",
      render: (month?: number) => month || "N/A",
    },
    {
      title: intl.formatMessage({
        id: "app.prospectuses.serial_number",
      }),
      dataIndex: "serial_number",
      key: "serial_number",
    },
    {
      title: intl.formatMessage({ id: "app.prospectuses.masav_exported_at" }),
      dataIndex: "masav_exported_at",
      key: "masav_exported_at",
      render: (text?: string | Date) =>
        text ? moment(text).format("DD/MM/YYYY") : "",
    },
    {
      title: intl.formatMessage({
        id: "app.prospectuses.is_self_participating",
      }),
      dataIndex: "is_self_participating",
      key: "is_self_participating",
      render: (text?: boolean) =>
        text ? (
          <CheckOutlined style={{ color: "green", fontSize: "16px" }} />
        ) : (
          <span>-</span>
        ),
    },
  ];

  const rowSelection: TableProps<Prospectus>["rowSelection"] = {
    type: "checkbox",
    onChange: (selectedRowKeys: React.Key[], selectedRows: Prospectus[]) => {
      setSelectedProspectuses(selectedRows.map((row) => row.id));
    },
  };

  return (
    <Modal
      closable={false}
      footer={null}
      maskClosable={false}
      title={null}
      width={addProspectuses ? "90%" : "500px"}
      style={{ top: 20 }}
      styles={{
        body: {
          padding: 0,
          maxHeight: "calc(100vh - 100px)",
          overflow: "hidden",
        },
      }}
      open={visible}
      modalRender={(modal) => <Draggable handle=".header">{modal}</Draggable>}
      destroyOnClose
      forceRender
    >
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <div
          className="header"
          style={{
            width: "100%",
            cursor: "move",
            padding: "16px 24px",
            borderBottom: "1px solid #f0f0f0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Button
            style={{ position: "absolute", left: 10, top: 10 }}
            className="closeBtn"
            type="text"
            shape="circle"
            icon={<CloseOutlined />}
            onClick={onCancel}
          />
          <Title level={4} style={{ margin: 0 }}>
            {title}
          </Title>
          <Button
            className="closeBtn"
            type="text"
            shape="circle"
            icon={<CloseOutlined />}
            onClick={onCancel}
          />
        </div>

        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "24px",
          }}
        >
          <Form<FormValues>
            {...layout}
            form={form}
            name="generate_masav_report_form"
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            onValuesChange={onValuesChange}
            initialValues={initialValues}
            autoComplete="off"
          >
            <Row gutter={20}>
              <Col span={addProspectuses ? 8 : 24}>
                <div>
                  <DatePickerItem
                    name="month"
                    category="component"
                    picker="month"
                    format={"MM/YYYY"}
                    required={true}
                  />

                  {!branchId && (
                    <BranchSelect
                      label={intl.formatMessage({
                        id: "app.common.branch",
                      })}
                      name="branchId"
                      category="component"
                      required={true}
                    />
                  )}
                  {!addProspectuses && (
                    <CheckBoxItem
                      showLabel={true}
                      name="incremental"
                      category="component"
                    />
                  )}

                  <CheckBoxItem
                    showLabel={true}
                    name="dry_run"
                    category="component"
                  />

                  {hasOrgAccess && (
                    <CheckBoxItem
                      showLabel={true}
                      name="add_prospectuses"
                      category="component"
                    />
                  )}
                </div>
              </Col>

              {addProspectuses && (
                <Col span={16}>
                  <AnimationChildWrapper
                    key="prospectus-table-animation"
                    itemKey={7}
                  >
                    <Table<Prospectus>
                      rowSelection={rowSelection}
                      columns={columns}
                      dataSource={prospectuses}
                      rowKey="id"
                      size="small"
                      scroll={{ y: 300 }}
                      pagination={false}
                      loading={loading && addProspectuses}
                      bordered
                    />
                  </AnimationChildWrapper>
                </Col>
              )}
            </Row>

            <Row>
              <Col span={24} style={{ textAlign: "center", marginTop: "24px" }}>
                <Button htmlType="submit" type="primary" loading={loading}>
                  {btnText}
                </Button>
              </Col>
            </Row>
          </Form>
        </div>
      </div>
    </Modal>
  );
};

export default GenerateMasavReportModal;
